import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  DatePicker,
  Flex,
  Image,
  Dropdown,
  App,
  Row,
  Col,
  Select,
  Radio,
  Input,
  Button,
  InputNumber,
} from "antd";
import DatePickerIcon from "../../assets/icons/datepicker-calendar.svg";
import { ArrowDownOutlined } from "@ant-design/icons";
import PdfIcon from "../../assets/icons/pdf-icon.svg";
import CsvIcon from "../../assets/icons/csv-icon.svg";
import dayjs from "dayjs";
import axios from "../../config/httpConfig";
import { getDownloadSuccessMessage } from "../../helpers/helpers";
import "./Reports.scss";

const { Option } = Select;
const { Text } = Typography;

// 🔹 Filters per report
const filtersByTitle = {
  "Deal wise status document": [
    "timePeriod",
    "businessName",
    "enterpriseName",
    "dealAmt",
    "discountRate",
    "dealStatus",
  ],
  "Deal wise Settlement": [
    "timePeriod",
    "businessName",
    "enterpriseName",
    "dealAmt",
    "settledAmt",
    "dealId",
  ],
  "Investor Ledger": ["timePeriod", "investorName", "txnAmt", "investorStatus"],
  "Business Ledger": ["timePeriod", "businessName", "txnAmt"],
  "Deal wise Profitability": ["timePeriod", "industryType"],
};

// 🔹 Labels
const filterLabels = {
  timePeriod: "Time Period",
  businessName: "Business Name",
  enterpriseName: "Enterprise Name",
  dealAmt: "Deal Amount",
  discountRate: "Interest Rate",
  dealStatus: "Deal Status",
  settledAmt: "Settled Amount",
  investorName: "Investor Name",
  txnAmt: "Amount",
  investorStatus: "Investor Status",
  industryType: "Industry Type",
  dealId: "Deal ID",
};

const ReportsCollapseContent = ({ type, title }) => {
  const { message } = App.useApp();
  const { reportsDropdownData } = useSelector((state) => state.reports);
  const [validationError, setValidationError] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("timePeriod");
  const [selectedValue, setSelectedValue] = useState({
    startDate: null,
    endDate: null,
    businessName: "",
    enterpriseName: "",
    dealAmtMin: "",
    dealAmtMax: "",
    discountRateMin: "",
    discountRateMax: "",
    dealStatus: "",
    settledAmtMin: "",
    settledAmtMax: "",
    investorName: "",
    txnAmtMin: "",
    txnAmtMax: "",
    investorStatus: "",
    industryType: "",
    dealId: "",
  });

  const isDiscountRateIncomplete =
    !selectedValue.discountRateMin || !selectedValue.discountRateMax;

  const isRangeIncomplete = ["dealAmt", "txnAmt", "settledAmt"].includes(
    selectedFilter,
  )
    ? !selectedValue[`${selectedFilter}Min`] ||
      !selectedValue[`${selectedFilter}Max`]
    : false;

  const isDateRangeIncomplete =
    selectedFilter === "timePeriod" &&
    (!selectedValue.startDate || !selectedValue.endDate);

  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  const validateRange = (min, max, label) => {
    if (isEmpty(min) || isEmpty(max)) {
      setValidationError("");
      return;
    }

    if (Number(min) > Number(max)) {
      setValidationError(
        `Minimum ${label} cannot be greater than Maximum ${label}`,
      );
    } else {
      setValidationError("");
    }
  };

  const handleInputChange = (name, value) => {
    setSelectedValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAmountKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];

    // Allow Ctrl/Cmd shortcuts
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(e.key.toLowerCase())
    ) {
      return;
    }

    const isNumber = /^[0-9]$/.test(e.key);

    if (!isNumber && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleAmountPaste = (e) => {
    const pasteValue = e.clipboardData.getData("text");

    if (!/^\d+$/.test(pasteValue)) {
      e.preventDefault();
    }
  };

  const validateDiscountRate = (min, max) => {
    const minNum = Number(min);
    const maxNum = Number(max);

    if (isEmpty(min) && isEmpty(max)) {
      setValidationError("");
      return;
    }

    if (!isEmpty(min) && (minNum < 10 || minNum > 50)) {
      setValidationError("Minimum rate must be between 10% and 50%");
      return;
    }

    if (!isEmpty(max) && (maxNum < 10 || maxNum > 50)) {
      setValidationError("Maximum rate must be between 10% and 50%");
      return;
    }

    if (!isEmpty(min) && !isEmpty(max) && minNum > maxNum) {
      setValidationError("Min rate cannot be greater than Max rate");
      return;
    }

    setValidationError("");
  };

  // 🔹 API download call
  const downloadReport = async (format, type) => {
    try {
      let endPoint = "";

      if (type === "1") endPoint = "dealWiseStatusDoc";
      else if (type === "2") endPoint = "dealWiseSettlement";
      else if (type === "3") endPoint = "investorLedger";
      else if (type === "4") endPoint = "businessLedger";
      else if (type === "5") endPoint = "dealWiseProfitability";

      const filters = {
        format,
        startDate: selectedValue?.startDate,
        endDate: selectedValue?.endDate,
        businessName: selectedValue?.businessName,
        enterpriseName: selectedValue?.enterpriseName,
        dealAmt: {
          min: selectedValue?.dealAmtMin,
          max: selectedValue?.dealAmtMax,
        },
        discountRate: {
          min:
            selectedValue?.discountRateMin !== ""
              ? Number(selectedValue.discountRateMin) / 100
              : undefined,
          max:
            selectedValue?.discountRateMax !== ""
              ? Number(selectedValue.discountRateMax) / 100
              : undefined,
        },
        dealStatus: selectedValue?.dealStatus,
        settledAmt: {
          min: selectedValue?.settledAmtMin,
          max: selectedValue?.settledAmtMax,
        },
        investorName: selectedValue?.investorName,
        investorStatus: selectedValue?.investorStatus,
        txnAmt: {
          min: selectedValue?.txnAmtMin,
          max: selectedValue?.txnAmtMax,
        },
        industryType: selectedValue?.industryType,
        dealId: selectedValue?.dealId,
      };

      // Clean empty filters
      const cleanFilters = (filters) => {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters)
            .map(([key, value]) => {
              if (typeof value === "object" && value !== null) {
                const cleanedValue = cleanFilters(value);
                if (Object.keys(cleanedValue).length === 0) return null;
                return [key, cleanedValue];
              }
              return value !== undefined &&
                value !== "" &&
                value !== null &&
                !Number.isNaN(value)
                ? [key, value]
                : null;
            })
            .filter(Boolean),
        );
        return cleanedFilters;
      };

      const filteredParams = cleanFilters(filters);

      const response = await axios.get(`/api/admin/${endPoint}`, {
        params: { ...filteredParams },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      const downloadedFileName = `${title}.${format === "pdf" ? "pdf" : "xlsx"}`;
      link.href = url;
      link.setAttribute("download", downloadedFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(getDownloadSuccessMessage(downloadedFileName));
    } catch (error) {
      message.error(
        error.message ||
          "Failed to download the report. Please try again later.",
      );
    }
  };

  const disableFutureDates = (current) =>
    current && current > dayjs().endOf("day");

  // 🔹 Render filter fields
  const renderFilterField = () => {
    switch (selectedFilter) {
      case "timePeriod":
        return (
          <Flex gap={10}>
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="Start Date"
              suffixIcon={
                <Image src={DatePickerIcon} alt="Calendar" preview={false} />
              }
              value={
                selectedValue.startDate ? dayjs(selectedValue.startDate) : null
              }
              onChange={(date) => {
                handleInputChange(
                  "startDate",
                  date ? date.format("YYYY-MM-DD") : null,
                );

                handleInputChange("endDate", null);
              }}
              disabledDate={disableFutureDates}
              style={{ width: "100%" }}
            />
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="End Date"
              suffixIcon={
                <Image src={DatePickerIcon} alt="Calendar" preview={false} />
              }
              value={
                selectedValue.endDate ? dayjs(selectedValue.endDate) : null
              }
              onChange={(date) =>
                handleInputChange(
                  "endDate",
                  date ? date.format("YYYY-MM-DD") : null,
                )
              }
              disabledDate={(current) => {
                // Disable future dates
                if (current && current > dayjs().endOf("day")) {
                  return true;
                }

                // Disable dates before the selected start date
                if (selectedValue.startDate) {
                  return current.isBefore(
                    dayjs(selectedValue.startDate),
                    "day",
                  );
                }

                return false;
              }}
              style={{ width: "100%" }}
            />
          </Flex>
        );

      // 🔹 Currency fields
      case "dealAmt":
      case "txnAmt":
      case "settledAmt":
        return (
          <Flex gap={20}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Min ${filterLabels[selectedFilter]}`}
              value={selectedValue[`${selectedFilter}Min`] || null}
              changeOnWheel={false}
              controls={false}
              maxLength={15}
              formatter={(value) =>
                value
                  ? new Intl.NumberFormat("en-IN", {
                      maximumFractionDigits: 0,
                    }).format(value)
                  : ""
              }
              parser={(value) => value?.replace(/\D/g, "")}
              onKeyDown={handleAmountKeyDown}
              onPaste={handleAmountPaste}
              onChange={(value) => {
                handleInputChange(`${selectedFilter}Min`, value ?? "");

                validateRange(
                  value,
                  selectedValue[`${selectedFilter}Max`],
                  filterLabels[selectedFilter],
                );
              }}
            />
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Max ${filterLabels[selectedFilter]}`}
              value={selectedValue[`${selectedFilter}Max`] || null}
              changeOnWheel={false}
              controls={false}
              maxLength={15}
              formatter={(value) =>
                value
                  ? new Intl.NumberFormat("en-IN", {
                      maximumFractionDigits: 0,
                    }).format(value)
                  : ""
              }
              parser={(value) => value?.replace(/\D/g, "")}
              onKeyDown={handleAmountKeyDown}
              onPaste={handleAmountPaste}
              onChange={(value) => {
                handleInputChange(`${selectedFilter}Max`, value ?? "");

                validateRange(
                  selectedValue[`${selectedFilter}Min`],
                  value,
                  filterLabels[selectedFilter],
                );
              }}
            />
          </Flex>
        );

      // 🔹 Percentage field
      case "discountRate":
        return (
          <Flex vertical gap={4}>
            <Flex gap={20}>
              <Input
                name="discountRateMin"
                placeholder="Min"
                suffix={<Text>%</Text>}
                value={selectedValue.discountRateMin}
                onKeyDown={handleAmountKeyDown}
                onChange={(e) => {
                  let cleanValue = e.target.value.replace(/[^0-9]/g, "");
                  handleInputChange("discountRateMin", cleanValue);

                  const num = Number(cleanValue);
                  validateDiscountRate(num, selectedValue.discountRateMax);
                }}
              />

              <Input
                name="discountRateMax"
                placeholder="Max"
                suffix={<Text>%</Text>}
                value={selectedValue.discountRateMax}
                onKeyDown={handleAmountKeyDown}
                onChange={(e) => {
                  let cleanValue = e.target.value.replace(/[^0-9]/g, "");
                  handleInputChange("discountRateMax", cleanValue);

                  const num = Number(cleanValue);
                  validateDiscountRate(selectedValue.discountRateMin, num);
                }}
              />
            </Flex>
          </Flex>
        );

      // 🔹 Dropdowns
      default:
        return (
          <Select
            style={{ width: "100%" }}
            placeholder={`Select ${filterLabels[selectedFilter]}`}
            value={selectedValue[selectedFilter] || undefined}
            onChange={(value) => handleInputChange(selectedFilter, value)}
          >
            {reportsDropdownData[selectedFilter]?.map((option, index) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
    }
  };

  return (
    <div className="collapse-item-content-container">
      <Row gutter={[16, 16]} align="middle" wrap>
        <Col span={24}>
          <Radio.Group
            value={selectedFilter}
            onChange={(e) => {
              setSelectedFilter(e.target.value);
              setSelectedValue({});
              setValidationError("");
            }}
            options={filtersByTitle[title]?.map((filter) => ({
              label: filterLabels[filter],
              value: filter,
            }))}
          />
        </Col>

        <Col xs={24} md={10} lg={4}>
          <Text style={{ color: "#676767", fontSize: 14 }}>
            {filterLabels[selectedFilter]}
          </Text>
        </Col>

        <Col xs={24} md={24} lg={14}>
          {renderFilterField()}
        </Col>

        <Col xs={24} md={24} lg={6} style={{ textAlign: "right" }}>
          <Dropdown
            menu={{
              items: [
                {
                  key: "1",
                  icon: (
                    <Image
                      width={20}
                      src={PdfIcon}
                      alt="PDF-Icon"
                      preview={false}
                    />
                  ),
                  label: (
                    <span
                      onClick={() => downloadReport("pdf", type)}
                      style={{ margin: 10 }}
                    >
                      Download as PDF
                    </span>
                  ),
                },
                { type: "divider" },
                {
                  key: "2",
                  icon: (
                    <Image
                      width={20}
                      src={CsvIcon}
                      alt="CSV-Icon"
                      preview={false}
                    />
                  ),
                  label: (
                    <span
                      onClick={() => downloadReport("xlsx", type)}
                      style={{ margin: 10 }}
                    >
                      Download as CSV
                    </span>
                  ),
                },
              ],
            }}
            trigger={["click"]}
            arrow={{ pointAtCenter: true }}
            placement="bottomCenter"
          >
            <Button
              variant="solid"
              color="primary"
              icon={<ArrowDownOutlined />}
              iconPosition="end"
              style={{ padding: 24 }}
              disabled={
                isDateRangeIncomplete ||
                (selectedFilter === "discountRate" &&
                  (validationError || isDiscountRateIncomplete)) ||
                (["dealAmt", "txnAmt", "settledAmt"].includes(selectedFilter) &&
                  (validationError || isRangeIncomplete))
              }
            >
              Download
            </Button>
          </Dropdown>
        </Col>
      </Row>
      {validationError && (
        <Text style={{ color: "red", fontSize: 12 }}>{validationError}</Text>
      )}
    </div>
  );
};

export default ReportsCollapseContent;
